import { Link } from 'react-router-dom'
import GShell from './GShell.jsx'
import HustlerGames from '../../components/HustlerGames.jsx'
import CLUB from '../../config/club.js'
import { PBCC_PREMIUM, GGC_ONLY, HUSTLER } from '../../config/siteMode.js'

// G-Features hub — the games engine + the handicap system, surfaced in one
// place with full access (the GShell access gate is open on the full build).
// Separate from the Pro Shop Suite (/admin/gg). Reached from the "G-Features"
// sidebar tab. Lists EVERY game/format that exists in the codebase.
const PRIMARY = CLUB.primaryColor || 'var(--ge-primary)'
const ACCENT = CLUB.accentColor || 'var(--ge-primary)'
// Headings + helper text sit on the dark panel → gold/light on premium.
// Golf Hustler: lime headings + soft-gray sub on the near-black panel.
const TITLE_ON_DARK = HUSTLER ? 'var(--ge-primary)' : (PBCC_PREMIUM ? 'var(--ge-gold)' : PRIMARY)
const SUB_ON_DARK = HUSTLER ? '#4b5563' : (PBCC_PREMIUM ? '#D8CFB8' : '#6B7280')

// The full restored games suite (from the 2026-07-08 backup) — each a real
// per-hole play engine with betting/settlement. Routed under /admin/gg/games/*.
const GAMES = [
  { to: '/admin/gg/games', icon: '▶️', label: 'Click Here to Learn Every Game', desc: 'Set up players + course and play — Wolf (Blind/Howl, Biggest Loser, Shuck) with live settlement and saved games.' },
  { to: '/admin/gg/games?start=wolf', icon: '🐺', label: 'Wolf', desc: 'Team up each hole or go lone wolf; Blind Wolf & Howl 2× stakes.' },
  { to: '/admin/gg/games?start=vegas', icon: '🎲', label: 'Vegas', desc: 'Las Vegas — combine partners’ scores into a two-digit number.' },
  { to: '/admin/gg/games?start=bbb', icon: '🔔', label: 'Bingo Bango Bongo', desc: 'Points for first on, closest once on, first in the hole.' },
  { to: '/admin/gg/games?start=snake', icon: '🐍', label: 'Snake', desc: 'Last three-putt holds the snake — pays out at the end.' },
  { to: '/admin/gg/games?start=dots', icon: '🔵', label: 'Dots (Garbage/Junk)', desc: 'Points for greenies, sandies, birdies, and more.' },
  { to: '/admin/gg/games?start=chicago', icon: '🏙️', label: 'Chicago', desc: 'Quota points system — beat your handicap-based target.' },
  { to: '/admin/gg/games?start=dogfight', icon: '🐕', label: 'Dogfight', desc: 'Points-based multi-player match.' },
  { to: '/admin/gg/games?start=nassau', icon: '🎡', label: 'Nassau', desc: 'Front, back and overall matches with auto-presses.' },
  { to: '/admin/gg/games?start=nines', icon: '9️⃣', label: 'Nines (9-point)', desc: 'Nine points split among three players each hole.' },
  { to: '/admin/gg/games?start=rabbit', icon: '🐇', label: 'Rabbit', desc: 'Win a hole to grab the rabbit; hold it at the turn/finish.' },
  { to: '/admin/gg/games?start=scotch', icon: '🥃', label: 'Scotch (Bridge)', desc: 'Points + presses team match.' },
  { to: '/admin/gg/games?start=shamble', icon: '🤝', label: 'Shamble', desc: 'Best drive then everyone plays their own ball in.' },
  { to: '/admin/gg/games?start=scramble', icon: '⛳', label: 'Scramble', desc: 'Everyone hits from the best shot — one team ball, low team total.' },
  { to: '/admin/gg/games?start=shuffle', icon: '🔀', label: 'Shuffle', desc: 'Rotating-partners team game.' },
  { to: '/admin/gg/games?start=sixes', icon: '6️⃣', label: 'Sixes (Round Robin)', desc: 'Partners rotate every six holes.' },
  { to: '/admin/gg/games?start=matchplay', icon: '⚔️', label: 'Match Play', desc: 'Hole-by-hole match, up/down/all-square.' },
  { to: '/admin/gg/games?start=stroke-play', icon: '📋', label: 'Stroke Play', desc: 'Straight gross/net stroke totals.' },
  { to: '/admin/gg/games?start=skins', icon: '💰', label: 'Skins', desc: 'Low score wins the hole; ties carry over. $/skin split-pot.' },
  { to: '/admin/gg/games?start=stableford', icon: '🎯', label: 'Stableford', desc: 'Points vs par — eagle 4, birdie 3, par 2, bogey 1.' },
  { to: '/admin/gg/games?start=scats', icon: '💵', label: 'Scats', desc: 'One scat per hole to the low under par; pot splits by scats.' },
  { to: '/admin/gg/games?start=team-cup', icon: '🏆', label: 'Team Cup', desc: 'Ryder-Cup-style team competition.' },
  { to: '/admin/gg/games?start=four-ball', icon: '⛳', label: 'Four-Ball', desc: 'Best-ball of two-player teams.' },
  { to: '/admin/gg/games/practice', icon: '🎯', label: 'Practice', desc: 'Practice-round scoring sandbox.' },
  { to: '/admin/gg/games/handicap', icon: '📐', label: 'Handicap', desc: 'Look up your handicap and see your course handicap & strokes — how handicaps keep every game fair.' },
]

// Playable side-game scoring pages (G-Formats engine) — each deep-linked so it
// opens directly on that game.
const SCORING_GAMES = [
  { to: '/admin/gg/formats?game=stableford', icon: '🎯', label: 'Stableford',
    desc: 'Standard or Modified/configurable points, Gross or Net.' },
  { to: '/admin/gg/formats?game=skins', icon: '💰', label: 'Skins',
    desc: 'Carryover, next-hole validation, pot value, Gross or Net.' },
  { to: '/admin/gg/formats?game=scramble', icon: '⛳', label: 'Scramble',
    desc: 'Team scramble with the USGA/GG allowance table, Gross or Net.' },
  { to: '/admin/gg/multi-format', icon: '🧮', label: 'Multi-Format Scorer',
    desc: 'Run any combination at once: 4-Ball Best Ball (G/N), Stableford (G/N), Skins (G/N), Scramble (G/N), Flight Winners, Match Results, Calcutta Payouts.' },
]

// Tournament playing formats — chosen when you create a tournament (Registration
// / New Tournament), not standalone pages.
const PLAYING_FORMATS = ['4-Ball Best Ball', 'Stroke Play Individual', 'Scramble', 'Shamble', 'Alternate Shot', 'Team Cup', 'Stableford']
// Scramble drive-rule variants (the Scramble setup picker).
const SCRAMBLE_VARIANTS = ['Standard', 'Texas', 'Florida', 'Shamble', 'Ambrose', 'Las Vegas']

const HANDICAP = [
  { to: '/admin/gg/games/handicap', icon: '📐', label: 'Handicap',
    desc: 'Your handicap index, course handicap and strokes — see how handicaps keep games fair. Enter your (GH)# to look it up.' },
]

// Golf Hustler top-nav jump targets → the card each link scrolls to.
const HH_ANCHORS = {
  '/admin/gg/season': 'hh-card-season',
  '/admin/gg/saved': 'hh-card-saved',
  '/admin/gg/history': 'hh-card-history',
  '/admin/gg/multigroup': 'hh-card-multigroup',
  '/admin/gg/games?start=wolf': 'hh-card-wolf',
}

function FCard({ item }) {
  // On the standalone GGC (GGC_ONLY) the cards are wider (2-col) → larger, more
  // readable title + description. The staff/pinebluffcc build keeps the original
  // sizes so it is untouched.
  const titleCls = GGC_ONLY ? 'text-xl font-extrabold' : 'text-base font-extrabold'
  const descCls  = GGC_ONLY ? 'text-base text-gray-600 mt-1 leading-snug' : 'text-sm text-gray-600 mt-1 leading-snug'
  const anchorId = HUSTLER ? HH_ANCHORS[item.to] : undefined
  // GGC standalone: h-full so every card fills its (equal-height, auto-rows-fr)
  // grid row → all cards the same height regardless of description length.
  return (
    <Link id={anchorId} to={item.to} className={`rounded-2xl p-4 block transition active:scale-[0.99] hover:shadow-lg${GGC_ONLY ? ' h-full' : ''}`}
      style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}44`, textDecoration: 'none' }}>
      <div className="flex items-start gap-3">
        <span className="text-2xl leading-none">{item.icon}</span>
        <div>
          <div className={titleCls} style={{ color: PRIMARY }}>{item.label}</div>
          <div className={descCls}>{item.desc}</div>
        </div>
      </div>
    </Link>
  )
}

function SectionRule({ label }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <span className="h-px w-6" style={{ background: ACCENT }} />
      <span className="text-xs font-extrabold uppercase tracking-widest" style={{ color: TITLE_ON_DARK }}>{label}</span>
      <span className="h-px flex-1" style={{ background: `${ACCENT}55` }} />
    </div>
  )
}

function Chip({ label }) {
  return (
    <span className="rounded-full px-3 py-1 text-sm font-bold" style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}55`, color: PRIMARY }}>
      {label}
    </span>
  )
}

export default function GFeaturesHub() {
  return (
    <GShell active="gfeatures">
      {HUSTLER && <HustlerGames />}
      {!HUSTLER && (
      <div className="space-y-6">
        {/* Golf Hustler carries the title + subtitle in its own hero; hide the
            duplicate here (and the section rule — its grid starts clean). */}
        {!HUSTLER && (
          <div>
            <h1 className="text-2xl font-extrabold" style={{ color: TITLE_ON_DARK }}>Golf Game Central</h1>
            <p className="text-sm mt-1" style={{ color: SUB_ON_DARK }}>{GGC_ONLY ? 'Play and bet your golf games — set up a game, pick your side bets, and settle up.' : 'The games engine and the handicap system — full access, no gate.'}</p>
          </div>
        )}

        <section>
          {!HUSTLER && <SectionRule label="Games — play & bet (full engine)" />}
          {/* 3 columns on both builds. On the GGC standalone the content area is
              widened (GShell max-w-7xl) so the full-width 3-col grid stretches
              across more of the page → each card is wider, still inside the
              existing left/right margin. */}
          <div id="hh-grid" className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-3${GGC_ONLY ? ' w-full auto-rows-fr' : ''}`}>
            {/* Season — its own preset season games (Team Stableford / Team Cup),
                separate from the one-off game tiles below. GGC standalone only. */}
            {GGC_ONLY && <FCard key="season" item={{ to: '/admin/gg/season', icon: '📅', label: 'Season', desc: 'Team Stableford or Team Cup across the season — running standings + a season pot.' }} />}
            {/* Members' Saved Games — a single tile among the game tiles; clicking
                it opens the member's saved-games list to reopen & edit. */}
            {GGC_ONLY && <FCard key="saved-games" item={{ to: '/admin/gg/saved', icon: '💾', label: 'Saved Games', desc: 'Open and edit the games you have saved.' }} />}
            {GGC_ONLY && <FCard key="multigroup" item={{ to: '/admin/gg/multigroup', icon: '📐', label: 'Handicap & Multi-Group', desc: 'Set up a group round — course, game, everyone\'s handicap, split into groups + share one link.' }} />}
            {/* History — a simple log of finished games (game, players, date),
                newest first. Fills the open utility-tile slot next to Saved Games. */}
            {GGC_ONLY && <FCard key="history" item={{ to: '/admin/gg/history', icon: '🕘', label: 'History', desc: 'Every game you\'ve finished — game type, players, and date, newest first.' }} />}
            {GAMES.filter((g) => !(GGC_ONLY && (g.to === '/admin/gg/games/handicap' || g.to === '/admin/gg/games/practice'))).map((g) => <FCard key={g.to} item={g} />)}
          </div>
        </section>

        {/* Side-game scoring (calculators) — read-only sample-data demos +
            staff/tournament Multi-Format (Flight Winners/Calcutta). Hidden on the
            standalone members' GGC (GGC_ONLY); still shown on the staff build. */}
        {!GGC_ONLY && (
          <section>
            <SectionRule label="Games — side-game scoring (calculators)" />
            <div className="grid sm:grid-cols-2 gap-3">{SCORING_GAMES.map((g) => <FCard key={g.to} item={g} />)}</div>
          </section>
        )}

        {/* Tournament playing formats + Scramble drive variants — hidden on the
            standalone members' GGC (GGC_ONLY); still shown on the staff build. */}
        {!GGC_ONLY && (
          <section>
            <SectionRule label="Tournament playing formats" />
            <div className="flex flex-wrap gap-2">{PLAYING_FORMATS.map((f) => <Chip key={f} label={f} />)}</div>
            <p className="text-xs mt-2 leading-snug" style={{ color: SUB_ON_DARK }}>Chosen when you create a tournament (Registration → New Tournament). Scored live through the main engine.</p>
          </section>
        )}

        {!GGC_ONLY && (
          <section>
            <SectionRule label="Scramble drive variants" />
            <div className="flex flex-wrap gap-2">{SCRAMBLE_VARIANTS.map((v) => <Chip key={v} label={v} />)}</div>
            <p className="text-xs mt-2 leading-snug" style={{ color: SUB_ON_DARK }}>Set in the Scramble tournament setup — how the team's drive is chosen each hole.</p>
          </section>
        )}

        {/* Handicap section (demo/explainer) — removed on the standalone members'
            GGC (GGC_ONLY); still shown on the staff build. */}
        {!GGC_ONLY && (
          <section>
            <SectionRule label="Handicap" />
            <div className="grid sm:grid-cols-2 gap-3">{HANDICAP.map((h) => <FCard key={h.to} item={h} />)}</div>
            <p className="text-xs mt-3 leading-snug" style={{ color: SUB_ON_DARK }}>
              Handicaps let a high- and a low-handicap player have a real match — the app applies your
              course handicap and strokes automatically so every game plays fair.
            </p>
          </section>
        )}
      </div>
      )}
    </GShell>
  )
}
